import { useVirtualizer } from '@tanstack/react-virtual';
import { Pause, Play, RotateCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { adminLogsGetAdminLogs } from '@/lib/api/admin-logs/admin-logs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { streamSse } from '@/lib/http/sseStream';

interface LogEntry {
  timestamp: string;
  level: string;
  messageTemplate: string;
  renderedMessage: string;
  exception?: string | null;
  properties: Record<string, string | null>;
}

type Mode = 'snapshot' | 'live';

const LEVELS: LogEntry['level'][] = ['Verbose', 'Debug', 'Information', 'Warning', 'Error', 'Fatal'];
const MAX_LIVE = 2000;

export default function LogsPage() {
  const [mode, setMode] = useState<Mode>('snapshot');
  const [paused, setPaused] = useState(false);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  // Snapshot on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    (async () => {
      try {
        const data = (await adminLogsGetAdminLogs({ limit: 500 })) as unknown as LogEntry[];
        if (!cancelled) setEntries(data);
      } catch (err) {
        if (!cancelled) setLoadError((err as Error).message ?? 'Could not load logs.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Live SSE tail.
  useEffect(() => {
    if (mode !== 'live' || paused) return;

    const unsubscribe = streamSse<LogEntry>({
      path: '/admin/logs/stream',
      onEvent: (event) => {
        setEntries((prev) => {
          const next = [event, ...prev];
          return next.length > MAX_LIVE ? next.slice(0, MAX_LIVE) : next;
        });
      },
      onError: (err) => setLoadError((err as Error)?.message ?? 'SSE stream error.'),
    });

    return () => unsubscribe();
  }, [mode, paused]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (levelFilter && e.level !== levelFilter) return false;
      if (needle && !e.renderedMessage.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [entries, levelFilter, search]);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 72,
    overscan: 10,
  });

  return (
    <section className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Logs</h1>
        <p className="text-sm text-muted-foreground">
          In-process Serilog ring buffer (Phase 7). Card data is masked before entries land here.
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center gap-3 space-y-0">
          <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
            <button
              className={`rounded px-3 py-1 ${mode === 'snapshot' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => setMode('snapshot')}
            >
              Snapshot
            </button>
            <button
              className={`rounded px-3 py-1 ${mode === 'live' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => setMode('live')}
            >
              Live tail
            </button>
          </div>
          <Input
            placeholder="Filter text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <Select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} className="w-36">
            <option value="">All levels</option>
            {LEVELS.map((lvl) => (
              <option key={lvl} value={lvl}>
                {lvl}
              </option>
            ))}
          </Select>
          <div className="ml-auto flex items-center gap-1">
            {mode === 'live' && (
              <Button variant="outline" size="sm" onClick={() => setPaused((p) => !p)} className="gap-2">
                {paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                {paused ? 'Resume' : 'Pause'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setEntries([])} className="gap-2">
              <Trash2 className="h-3 w-3" />
              Clear
            </Button>
            {mode === 'snapshot' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  const data = (await adminLogsGetAdminLogs({ limit: 500 })) as unknown as LogEntry[];
                  setEntries(data);
                }}
                className="gap-2"
              >
                <RotateCw className="h-3 w-3" />
                Refresh
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loadError ? (
            <div className="p-6 text-sm text-destructive">{loadError}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No log entries match the current filter.
            </div>
          ) : (
            <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto border-t border-border">
              <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
                {virtualizer.getVirtualItems().map((v) => {
                  const entry = filtered[v.index];
                  return (
                    <div
                      key={v.key}
                      className="absolute left-0 right-0 border-b border-border px-4 py-3 text-xs"
                      style={{ transform: `translateY(${v.start}px)`, height: v.size }}
                    >
                      <LogRow entry={entry} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground">
        Showing {filtered.length} of {entries.length} buffered entries.
      </div>
    </section>
  );
}

function LogRow({ entry }: { entry: LogEntry }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-32 shrink-0 font-mono tabular-nums text-muted-foreground">
        {new Date(entry.timestamp).toLocaleTimeString([], { hour12: false })}
      </span>
      <LevelBadge level={entry.level} />
      <div className="min-w-0 flex-1">
        <div className="truncate">{entry.renderedMessage}</div>
        {entry.exception && (
          <details className="mt-1 text-[10px] text-destructive">
            <summary className="cursor-pointer">Stack trace</summary>
            <pre className="mt-1 whitespace-pre-wrap font-mono">{entry.exception}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

function LevelBadge({ level }: { level: string }) {
  const base = 'w-20 justify-center';
  if (level === 'Fatal' || level === 'Error') return <Badge variant="destructive" className={base}>{level}</Badge>;
  if (level === 'Warning') return <Badge variant="warning" className={base}>{level}</Badge>;
  if (level === 'Information') return <Badge variant="secondary" className={base}>{level}</Badge>;
  return <Badge variant="outline" className={base}>{level}</Badge>;
}
